/* global window */

import log from 'loglevel';
import loadJS from 'load-js';
import get from 'lodash/get';

import settings from './settings';

/**
 * Class representing Sailthru.
 */
class Sailthru {
  /**
   * Create a Sailthru instance.
   * @param {Object} params - Sailthru configuration parameters.
   */
  constructor(parms) {
    this.parms = parms;
    this.currentUserData = {};
    this.usedSignedUp = false;
  }

  /**
   * Check if Sailthru global script is ready.
   * @returns {Boolean}
   */
  get isSailthruReady() {
    return typeof window.Sailthru !== 'undefined';
  }

  /**
   * Load Sailthru global script.
   * @returns {Promise}
   */
  load() {
    const scriptsFound = typeof get(settings, 'sailthru.scripts') !== 'undefined';
    const customerIdFound = typeof get(settings, 'sailthru.customerId') !== 'undefined';

    if (!scriptsFound || !customerIdFound) {
      log.debug('Sailthru settings not found.');
      return Promise.resolve();
    }

    const scripts = get(settings, 'sailthru.scripts', []);
    const customerId = get(settings, 'sailthru.customerId', '');

    if (scripts.length !== 1 || !customerId.length) {
      log.debug('Invalid Sailthru settings.');
      return Promise.resolve();
    }

    return loadJS({
      url: settings.sailthru.scripts,
      async: true,
    });
  }

  /**
   * Initialize Sailthru global script.
   */
  init() {
    if (!this.isSailthruReady) {
      log.error('Tried to initialize Sailthru before the script was ready.');
      return;
    }

    window.Sailthru.init({
      customerId: settings.sailthru.customerId,
    });

    if (window.VLCL) {
      this.registerVLWindowMessageHandler();
    }

    if (window.HRST) {
      this.registerMediaOSWindowMessageHandler();
    }

    log.debug('Sailthru initialized.');
  }

  /**
   * Register a window message event to capture VL iframe messages.
   */
  registerVLWindowMessageHandler() {
    window.addEventListener('message', event => {
      if (this.usedSignedUp
        || event.origin !== window.location.origin
        || typeof event.data !== 'string'
        || event.data.indexOf('sailthru:email:') !== 0) {
        return;
      }

      const email = get(event.data.split('sailthru:email:'), '1', '');

      if (!email) {
        return;
      }

      this.usedSignedUp = true;

      const siteId = get(settings, 'site_id', 'vl_nola');
      const lists = {};
      const vars = {};
      lists[siteId] = 1;
      vars[siteId] = 'true';

      this.userSignUp(email, lists, 'site-capture-modal', vars)
        .then(event.source.postMessage('sailthru:userSignUp:success', '*'))
        .catch(event.source.postMessage('sailthru:userSignUp:error', '*'));
    }, false);
  }

  /**
   * Register a window message event to capture MediaOS iframe messages.
   */
  registerMediaOSWindowMessageHandler() {
    window.addEventListener('message', event => {
      if (event.origin !== window.location.origin
        || typeof event.data !== 'string'
        || event.data.indexOf('sailthru:email:') !== 0) {
        return;
      }

      const siteId = get(settings, 'site_id');
      const email = get(event.data.split('sailthru:email:'), '1', '');

      if (!siteId || !email) {
        event.source.postMessage('sailthru:breakingNews:error', '*');
        return;
      }

      const lists = {};
      const vars = {};
      lists[get(settings, 'sailthru.emailListTarget', `${siteId}_news_local_breaking`)] = 1;

      log.debug(`Sailthru BreakingNews SignUp [${JSON.stringify(lists)}]`);

      this.userSignUp(email, lists, 'site-capture-modal', vars)
        .then(event.source.postMessage('sailthru:breakingNews:success', '*'))
        .catch(event.source.postMessage('sailthru:breakingNews:error', '*'));
    }, false);
  }

  /**
   * Calls Sailthru sign up method and return the response to source window.
   * @param {String} email - user email.
   * @returns {Promise}
   */
  userSignUp(email, lists, source, vars = {}) {
    log.debug('Sailthru userSignUp');
    return new Promise((resolve, reject) => {
      window.Sailthru.integration('userSignUp', {
        email,
        lists,
        source,
        vars,
        onSuccess() {
          log.debug('Sailthru userSignUp success');
          resolve();
        },
        onError() {
          log.debug('Sailthru userSignUp error');
          reject();
        },
      });
    });
  }

  /**
   * Fetches known user email from Sailthru.
   * @returns {Promise}
   */
  fetchCurrentUserData() {
    const result = window.Sailthru.integration('getCurrentUser', {
      hash: 'sha256',
      onSuccess: response => {
        this.currentUserData = response;
        log.debug(`Sailthru userEmailHash = [${this.userEmailHash}]`);
      },
    });

    // Sailthru getCurrentUser returns false when cookie `sailthru_hid` is not available.
    if (typeof result !== 'object') {
      return Promise.resolve();
    }

    return result;
  }

  /**
   * Return a SHA-256 hash of the current user's email.
   * @returns {String}
   */
  get userEmailHash() {
    return this.currentUserData.emailHash;
  }
}

export default Sailthru;



// WEBPACK FOOTER //
// ./src/Sailthru.js